import { useEffect, useRef, useCallback, MutableRefObject, useState } from 'react';
import { useSpeechToText } from './useSpeechToText';
import { useBotAudio } from './useBotAudio';

/** Tối đa thời gian một lần bật mic (STT); dừng và gửi nếu đã có text. */
export const VOICE_CAPTURE_MAX_DURATION_MS = 10_000;

interface UseVoiceInteractionControllerProps {
  isThinking: boolean;
  isAudioEnabled: boolean;
  onVoiceInputSubmit: (text: string) => void;
  onChangeInput: (text: string) => void;
  resetInput: () => void;
  /** Sau khoảng này không nói → stop + gửi nếu có nội dung (Web Speech không cắt theo giây audio). */
  silenceTimeoutMs?: number;
  /** Trần thời gian thu từ lúc bật mic; tới hạn → stop + gửi nếu có nội dung. */
  maxDurationMs?: number;
  onMicrophoneError?: (msg: string) => void;
}

interface UseVoiceInteractionControllerReturn {
  listening: boolean;
  isBotSpeaking: boolean;
  hasPendingAudio: boolean;
  isSTTSupported: boolean;
  playBotAudio: (audio: string) => void;
  stopBotSpeaking: () => void;
  handleMicrophoneClick: () => void;
  stopMicAndDiscard: () => void;
}

export const useVoiceInteractionController = ({
  isThinking,
  isAudioEnabled,
  onVoiceInputSubmit,
  onChangeInput,
  resetInput,
  silenceTimeoutMs = 3000,
  maxDurationMs = VOICE_CAPTURE_MAX_DURATION_MS,
  onMicrophoneError,
}: UseVoiceInteractionControllerProps): UseVoiceInteractionControllerReturn => {
  // TTS Setup
  const { play: playBotAudio, stop: stopBotSpeaking, isSpeaking: isBotSpeaking, hasPendingAudio } = useBotAudio({
    isEnabled: isAudioEnabled,
  });

  // Track if mic was paused due to bot thinking (for auto-resume)
  const micPausedForThinkingRef = useRef(false);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceAutoSentRef = useRef(false);

  // Buffer input for auto-submit
  const currentInputRef = useRef('');

  // STT Setup
  const { start: startSTT, stop: stopSTT, stopAndDiscard: stopAndDiscardSTT, isListening: listening, isSupported: isSTTSupported } = useSpeechToText({
    onResult: (text: string) => {
      console.log('[VoiceController] STT Result:', text);
      currentInputRef.current = text;
      onChangeInput(text);
    },
    silenceTimeoutMs,
    maxDurationMs,
    onSilenceTimeout: () => {
      micPausedForThinkingRef.current = false;
      
      const currentInput = currentInputRef.current;
      if (currentInput && currentInput.trim()) {
        silenceAutoSentRef.current = true;
        console.log('[VoiceController] Auto-stop (silence or max duration) - sending:', currentInput);
        onVoiceInputSubmit(currentInput);
        resetInput();
        currentInputRef.current = ''; // clear buffer
      } else {
        console.log('[VoiceController] Auto-stop - no content to send');
      }
    },
    onError: (msg: string) => {
      onMicrophoneError?.(msg);
    },
  });

  const listeningRef = useRef(listening);
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  // ========== CONFLICT RESOLUTION ==========

  // Rule 1: Khi bot bắt đầu phát loa mà mic đang thu → Dừng mic
  useEffect(() => {
    if (isBotSpeaking && listening) {
      console.log('[VoiceController Conflict] Bot started speaking while mic is active → Stopping mic');
      stopSTT();
    }
  }, [isBotSpeaking, listening, stopSTT]);

  // Rule 2: Mic auto-pause khi chatbot đang thinking (chưa phản hồi)
  useEffect(() => {
    if (silenceAutoSentRef.current) {
      return;
    }

    if (isThinking && listening) {
      console.log('[VoiceController Mic] Chatbot thinking - pausing mic');
      micPausedForThinkingRef.current = true;
      stopSTT();

      // 30s timeout: resume mic nếu chatbot không response
      thinkingTimeoutRef.current = setTimeout(() => {
        console.log('[VoiceController Mic] 30s thinking timeout - resuming mic');
        if (micPausedForThinkingRef.current) {
          micPausedForThinkingRef.current = false;
          startSTT();
        }
      }, 30000);
    }

    if (!isThinking && micPausedForThinkingRef.current) {
      console.log('[VoiceController Mic] Chatbot responded - resuming mic');
      micPausedForThinkingRef.current = false;
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
      
      // Delay nhỏ để bot audio bắt đầu phát trước khi resume mic
      setTimeout(() => {
        if (!listeningRef.current) {
          startSTT();
        }
      }, 500);
    }
  }, [isThinking, listening, startSTT, stopSTT]);

  // Cleanup thinking timeout
  useEffect(() => {
    return () => {
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
      }
    };
  }, []);

  // Listen to skip-typewriter-animation event (user stopped/paused streaming)
  useEffect(() => {
    const handleSkipAnimation = () => {
      console.log('[VoiceController Mic] skip-typewriter-animation event detected - resetting micPausedForThinkingRef');
      micPausedForThinkingRef.current = false;
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
      stopSTT();
    };

    window.addEventListener('skip-typewriter-animation', handleSkipAnimation);
    return () => {
      window.removeEventListener('skip-typewriter-animation', handleSkipAnimation);
    };
  }, [stopSTT]);

  // Handle User Interactions
  const handleMicrophoneClick = useCallback(() => {
    console.log('[VoiceController Mic] Click, listening:', listening);
    if (listening) {
      console.log('[VoiceController Mic] Stopping STT (user clicked)');
      micPausedForThinkingRef.current = false;
      stopSTT();
    } else {
      console.log('[VoiceController Mic] Starting STT');
      if (isBotSpeaking) {
        console.log('[VoiceController Conflict] User starting mic while speaker is playing → Stopping speaker');
        stopBotSpeaking();
      }

      micPausedForThinkingRef.current = false;
      silenceAutoSentRef.current = false;
      currentInputRef.current = '';
      startSTT();
    }
  }, [listening, startSTT, stopSTT, stopBotSpeaking, isBotSpeaking]);

  const stopMicAndDiscard = useCallback(() => {
    console.log('[VoiceController Mic] Stop and discard (manual send/cancel)');
    silenceAutoSentRef.current = true; // Không auto-resume
    micPausedForThinkingRef.current = false;
    stopAndDiscardSTT();
  }, [stopAndDiscardSTT]);

  return {
    listening,
    isBotSpeaking,
    hasPendingAudio,
    isSTTSupported,
    playBotAudio,
    stopBotSpeaking,
    handleMicrophoneClick,
    stopMicAndDiscard,
  };
};

export default useVoiceInteractionController;
